import React from 'react';
import useApi from '../../hooks/useApi';
import { getDashboard } from '../../api/dashboard';
import TopBar from '../layout/TopBar';
import StatCard from './StatCard';
import CriticalTasksList from './CriticalTasksList';
import OpenRisksList from './OpenRisksList';
import RecentEntriesList from './RecentEntriesList';
import LoadingState from '../shared/LoadingState';
import ErrorState from '../shared/ErrorState';
import { formatDateTime } from '../../utils/dates';

function DashboardView({ onEntryClick, onNavigate }) {
  const { data, loading, error, refetch } = useApi(getDashboard, []);

  return (
    <div className="view-container">
      <TopBar title="Dashboard">
        <button className="btn-secondary" onClick={refetch}>↺</button>
      </TopBar>

      <div className="view-content">
        {loading && (
          <>
            <LoadingState type="stats" />
            <LoadingState rows={3} />
          </>
        )}
        {error && <ErrorState message={error} onRetry={refetch} />}
        {data && (
          <>
            <div className="stats-grid">
              <StatCard
                label="PREDICADOS"
                value={data.entries?.total}
                sub={`+${data.entries?.today ?? 0} hoy`}
                accentColor="var(--accent)"
              />
              <StatCard
                label="TAREAS ACTIVAS"
                value={(data.tasks?.pending ?? 0) + (data.tasks?.in_progress ?? 0)}
                sub={`${data.tasks?.in_progress ?? 0} en progreso`}
                accentColor="var(--warning)"
              />
              <StatCard
                label="RIESGOS ABIERTOS"
                value={data.risks?.total_open}
                sub={`${data.risks?.by_impact?.alto ?? 0} impacto alto`}
                accentColor="var(--danger)"
              />
              <StatCard
                label="PERSONAS"
                value={data.people?.total_unique}
                sub={data.people?.most_mentioned?.[0] ? `Top: ${data.people.most_mentioned[0].name}` : ''}
                accentColor="var(--purple)"
              />
            </div>

            <div className="dashboard-grid">
              <section className="card">
                <h3 className="card__title">Tareas críticas pendientes</h3>
                <CriticalTasksList
                  tasks={data.tasks?.critical_pending}
                  onEntryClick={onEntryClick}
                />
              </section>
              <section className="card">
                <h3 className="card__title">Riesgos de alto impacto</h3>
                <OpenRisksList
                  risks={data.risks?.critical_open}
                  onEntryClick={onEntryClick}
                />
              </section>
            </div>

            {data.contexts?.most_active?.length > 0 && (
              <section className="card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <h3 className="card__title" style={{ margin: 0 }}>Contextos activos</h3>
                  <button className="btn-link" style={{ fontSize: 13 }} onClick={() => onNavigate?.('contexts')}>Ver todos →</button>
                </div>
                {data.contexts.most_active.map(c => (
                  <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--border)', fontSize: 14 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ color: '#6C8EFF' }}>▌</span>
                      <span>{c.name}</span>
                      <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>· {c.pending_tasks} tareas</span>
                    </div>
                    {c.last_activity && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{formatDateTime(c.last_activity)}</span>}
                  </div>
                ))}
              </section>
            )}

            {data.decisions?.pending > 0 && (
              <section className="card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <h3 className="card__title" style={{ margin: 0 }}>Decisiones pendientes</h3>
                  <button className="btn-link" style={{ fontSize: 13 }} onClick={() => onNavigate?.('decisions')}>Ver todas →</button>
                </div>
                <div style={{ fontSize: 14, color: 'var(--text-muted)' }}>
                  🎯 {data.decisions.pending} decisión{data.decisions.pending !== 1 ? 'es' : ''} pendiente{data.decisions.pending !== 1 ? 's' : ''} de tu atención
                </div>
              </section>
            )}

            <section className="card">
              <h3 className="card__title">Entradas recientes</h3>
              <RecentEntriesList
                entries={data.recent_entries}
                onEntryClick={onEntryClick}
              />
            </section>
          </>
        )}
      </div>
    </div>
  );
}

export default DashboardView;
